import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import {
    Alert,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Divider,
    Paper,
    Stack,
    TextField,
    Typography,
} from "@mui/material";
import { Add, Check, ContentCopy, Link as LinkIcon, Refresh } from "@mui/icons-material";
import Api from "../../customlib/Api";

// DEV-981 — Blok "Link Tukar Diskon" di profil customer. Link personal
// (order.prestisa.id/poin/:shortToken) yang dikirim CS ke customer lewat WA
// untuk checkout pakai saldo poin. Data & aturan ada di Sakura BE:
// - GET  poin-checkout-links/customer/:id  → { link, can_generate }
// - POST poin-checkout-links                → generate (hanya customer yang pernah order)
// - POST poin-checkout-links/:id/regenerate → matikan link lama, terbitkan link baru
export default function PoinCheckoutLinkSection({ customerId }) {
    const [link, setLink] = useState(null);
    const [canGenerate, setCanGenerate] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");
    const [copied, setCopied] = useState(false);
    const [isConfirmRegenerateOpen, setIsConfirmRegenerateOpen] = useState(false);

    const fetchLink = useCallback(() => {
        setIsLoading(true);
        setErrorMessage("");
        axios
            .get(Api.poinCheckoutLinkByCustomer(customerId), Api.getRequestHeaderNoAuthNew())
            .then((response) => {
                const data = response.data?.data;
                setLink(data?.link ?? null);
                setCanGenerate(!!data?.can_generate);
            })
            .catch(() => {
                setErrorMessage("Gagal memuat Link Tukar Diskon.");
            })
            .finally(() => setIsLoading(false));
    }, [customerId]);

    useEffect(() => {
        if (customerId > 0) fetchLink();
    }, [customerId, fetchLink]);

    const submit = (request) => {
        setIsSubmitting(true);
        setErrorMessage("");
        request
            .then((response) => {
                setLink(response.data?.data ?? null);
                setCopied(false);
            })
            .catch((error) => {
                setErrorMessage(
                    error?.response?.data?.message || "Gagal memproses Link Tukar Diskon."
                );
            })
            .finally(() => setIsSubmitting(false));
    };

    const handleGenerate = () => {
        submit(
            axios.post(
                Api.poinCheckoutLinkCreate(),
                { customer_id: customerId },
                Api.getRequestHeaderNoAuthNew()
            )
        );
    };

    const handleRegenerate = () => {
        setIsConfirmRegenerateOpen(false);
        submit(
            axios.post(
                Api.poinCheckoutLinkRegenerate(link.id),
                {},
                Api.getRequestHeaderNoAuthNew()
            )
        );
    };

    const handleCopy = async () => {
        try {
            await navigator.clipboard.writeText(link?.share_url || "");
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch {
            // Clipboard API bisa gagal (mis. bukan secure context) — CS tetap bisa salin manual dari field.
        }
    };

    return (
        <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, mb: 2 }}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                <LinkIcon fontSize="small" color="action" />
                <Typography variant="subtitle2" fontWeight={700}>Link Tukar Diskon</Typography>
            </Stack>
            <Divider sx={{ mb: 1.5 }} />

            {isLoading ? (
                <Stack alignItems="center" sx={{ py: 1 }}>
                    <CircularProgress size={20} />
                </Stack>
            ) : (
                <Stack spacing={1.5}>
                    {errorMessage && <Alert severity="error">{errorMessage}</Alert>}

                    {link ? (
                        <>
                            <TextField
                                label="Link Tukar Diskon"
                                value={link.share_url}
                                size="small"
                                fullWidth
                                onFocus={(e) => e.target.select()}
                                InputProps={{ readOnly: true }}
                            />
                            <Stack direction="row" spacing={1}>
                                <Button
                                    variant="contained"
                                    startIcon={copied ? <Check fontSize="small" /> : <ContentCopy fontSize="small" />}
                                    onClick={handleCopy}
                                    disabled={isSubmitting}
                                    sx={{ textTransform: "none" }}
                                >
                                    {copied ? "Tersalin" : "Salin Link"}
                                </Button>
                                <Button
                                    variant="outlined"
                                    startIcon={<Refresh fontSize="small" />}
                                    onClick={() => setIsConfirmRegenerateOpen(true)}
                                    disabled={isSubmitting || !canGenerate}
                                    sx={{ textTransform: "none" }}
                                >
                                    Regenerate
                                </Button>
                            </Stack>
                        </>
                    ) : (
                        <>
                            {!canGenerate && (
                                <Typography variant="body2" color="text.secondary">
                                    Link Tukar Diskon hanya bisa dibuat untuk customer yang sudah pernah order.
                                </Typography>
                            )}
                            <Button
                                variant="contained"
                                startIcon={<Add fontSize="small" />}
                                onClick={handleGenerate}
                                disabled={isSubmitting || !canGenerate}
                                sx={{ textTransform: "none", alignSelf: "flex-start" }}
                            >
                                Generate Link Tukar Diskon
                            </Button>
                        </>
                    )}
                </Stack>
            )}

            <Dialog open={isConfirmRegenerateOpen} onClose={() => setIsConfirmRegenerateOpen(false)}>
                <DialogTitle>Regenerate Link Tukar Diskon?</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Link lama akan langsung tidak aktif dan tidak bisa dibuka lagi oleh customer.
                        Kirim ulang link baru ke customer setelah regenerate.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setIsConfirmRegenerateOpen(false)} sx={{ textTransform: "none" }}>
                        Batal
                    </Button>
                    <Button variant="contained" onClick={handleRegenerate} sx={{ textTransform: "none" }}>
                        Regenerate
                    </Button>
                </DialogActions>
            </Dialog>
        </Paper>
    );
}
